import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sun, Archive, PenLine, Network, Landmark, FileText, Briefcase, ShoppingCart,
  UserCheck, FileUp, Globe2, Smartphone, Route as RouteIcon, ScanLine,
  Rows3, KeyRound, Lock, ArrowRight, Check, ShieldCheck,
} from "lucide-react";
import { Reveal, SectionHeading, Counter, Sparkline, Corners } from "@/components/Reveal";
import { ProcessSteps } from "@/components/vision/shared";

const PROBLEMS = [
  { icon: Sun, title: "Long lines under the sun", desc: "Citizens waiting. Repeated paperwork. Delayed approvals." },
  { icon: Archive, title: "Records buried in cabinets", desc: "Decades of documents no one can search when it matters." },
  { icon: PenLine, title: "A mayor signing blind", desc: "Approvals pile up with no way to track what's underneath them." },
  { icon: Network, title: "A dozen disconnected offices", desc: "Permits, tax, barangay documents, procurement — each in its own silo." },
];

const MODULES = [
  { icon: Landmark, title: "Property Tax" },
  { icon: FileText, title: "Barangay Documents" },
  { icon: Briefcase, title: "Business Permits" },
  { icon: ShoppingCart, title: "Procurement" },
];

const ACCOUNTABILITY = [
  { icon: UserCheck, title: "Every project has a head", desc: "Clear ownership — one accountable person per project, named on the record." },
  { icon: FileUp, title: "Reports flow in", desc: "Documents, photos, real progress — straight from the field into the system." },
  { icon: Globe2, title: "One click — it's public", desc: "Publish proof of work for the people you serve." },
];

const CITIZENS = [
  { icon: Smartphone, title: "Apply from their phone, 24/7", desc: "Government service that never closes." },
  { icon: RouteIcon, title: "Track every step", desc: "No guessing, no chasing — every application shows exactly where it stands." },
  { icon: ScanLine, title: "Scan-verified documents", desc: "Every issued document verifies instantly — impossible to fake." },
];

const PAY_STEPS = [
  { n: "01", title: "Citizen pays", desc: "Directly into the LGU's own bank account, then uploads proof of payment." },
  { n: "02", title: "Treasurer confirms", desc: "The treasurer verifies the money really landed — before anything moves." },
  { n: "03", title: "Receipt is issued", desc: "Only after confirmation. Recorded permanently, impossible to erase." },
];

const WALLS = [
  { icon: Rows3, title: "Row-Level Security", desc: "Every record is sealed inside the database to exactly who may see it. A citizen sees only their own — never anyone else's." },
  { icon: KeyRound, title: "Five-Step Role Gate", desc: "Identity, account status, and role checked on every request. No one can promote themselves — the system refuses it automatically." },
  { icon: Lock, title: "Immutable Audit Trail", desc: "Logs and receipts are written once and can never be erased. No hidden edits, no history rewritten in the dark." },
];

const SECURITY_STRIP = [
  "AES-256 encryption — SOC 2-certified platform (Supabase / PostgreSQL)",
  "Each town gets its own isolated database — zero shared tables with any other LGU",
  "Daily backups with point-in-time recovery",
  "Intrusion attempts blocked and logged automatically",
];

const ROLLOUT = [
  { n: "01", range: "Day 1", title: "Public side goes live", desc: "Citizens can start transacting immediately." },
  { n: "02", range: "Weeks 1–4", title: "Fitted to your municipality", desc: "Offices, fees, forms, and workflows." },
  { n: "03", range: "Weeks 5–8", title: "Administrator trained personally", desc: "Your system administrator learns first — then guides your staff." },
  { n: "04", range: "Ongoing", title: "Video playbooks", desc: "Simple guides for every task. No one gets left behind." },
];

const SAMPLE_APPROVALS = [
  { id: "A-1042", label: "Business permit renewal — Brgy. 04" },
  { id: "A-1043", label: "Building clearance — Brgy. 11" },
  { id: "A-1044", label: "Procurement award — road materials" },
];

const CommandCenter = () => {
  const [approved, setApproved] = useState({});
  return (
    <div className="relative border border-white/10 bg-mac-surface/80 p-4 backdrop-blur-md sm:p-5" data-testid="laic-dashboard">
      <Corners />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">LAIC / Mayor's Command Center</span>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
          <span className="h-1.5 w-1.5 animate-blink rounded-full bg-emerald-400" /> Live
        </span>
      </div>

      <div className="mb-4 space-y-2.5">
        {SAMPLE_APPROVALS.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-3 border border-white/10 bg-mac-base/60 px-4 py-3">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{a.id}</p>
              <p className="text-sm text-slate-200">{a.label}</p>
            </div>
            <button
              data-testid={`laic-approve-${a.id}`}
              onClick={() => setApproved({ ...approved, [a.id]: true })}
              className={`flex shrink-0 items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-[background-color,color,border-color] duration-300 ${
                approved[a.id]
                  ? "border border-emerald-400/50 bg-emerald-400/10 text-emerald-400"
                  : "bg-mac-accent text-white hover:brightness-125"
              }`}
            >
              <Check className="h-3 w-3" /> {approved[a.id] ? "Signed" : "Approve & sign"}
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="border border-white/10 bg-mac-base/60 p-3.5" data-testid="laic-stat-pending">
          <p className="font-display text-2xl font-bold text-amber-400"><Counter to={128} /></p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Pending approvals</p>
        </div>
        <div className="border border-white/10 bg-mac-base/60 p-3.5" data-testid="laic-stat-collections">
          <p className="font-display text-2xl font-bold text-mac-accent">₱<Counter to={4.2} decimals={1} suffix="M" /></p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Collections today</p>
        </div>
        <div className="border border-white/10 bg-mac-base/60 p-3.5" data-testid="laic-stat-permits">
          <p className="font-display text-2xl font-bold text-white"><Counter to={312} /></p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Permits this month</p>
        </div>
        <div className="border border-white/10 bg-mac-base/60 p-3.5" data-testid="laic-stat-barangays">
          <p className="font-display text-2xl font-bold text-white">22/22</p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Barangays online</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border border-white/10 bg-mac-base/60 px-3 py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Collections / week</span>
        <Sparkline className="h-5 w-24 text-mac-accent" points="0,16 12,12 24,14 36,8 48,11 60,5 72,8 84,3" />
      </div>

      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600">Illustrative sample data — not a real municipality</p>
    </div>
  );
};

export default function Laic() {
  return (
    <div data-testid="laic-page">
      <section className="noise relative overflow-hidden border-b border-white/5 pb-20 pt-36 lg:pb-28 lg:pt-48">
        <div className="absolute inset-0 -z-10 bg-grid" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <p className="mb-6 inline-flex items-center gap-2 border border-mac-accent/40 bg-mac-accent/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-mac-accent">
              <Landmark className="h-3.5 w-3.5" /> Products / LAIC — LGU AI Companion
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 data-testid="laic-heading" className="max-w-4xl font-display text-5xl font-bold uppercase leading-[0.95] tracking-wide sm:text-6xl lg:text-7xl">
              Your whole city.<br /><span className="text-mac-accent">Online.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
              LAIC is the complete e-governance platform that brings every LGU office, service, and
              transaction into one intelligent system. Technology should simplify government — not complicate it.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="laic-cta-demo" className="btn-primary">
                Let's bring your city online <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="01"
            eyebrow="Before LAIC"
            title={<>These aren't technology problems.<br />They're <span className="text-mac-accent">workflow problems</span>.</>}
            testId="laic-problem-heading"
          />
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1} className="bg-mac-base">
                <div className="card-tech h-full border-0" data-testid={`laic-problem-${i}`}>
                  <p.icon className="mb-6 h-6 w-6 text-amber-400" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wider text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-14 max-w-3xl border-l-2 border-mac-accent pl-8 font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-white sm:text-3xl" data-testid="laic-problem-quote">
              "We've all just accepted this — like government was never supposed to be better.{" "}
              <span className="text-mac-accent">It was. And now, it is.</span>"
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-mac-surface/30 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="02"
            eyebrow="One System"
            title={<>Not a dozen offices.<br /><span className="text-mac-accent">One system</span> — everything talks to everything.</>}
            testId="laic-unifies-heading"
          />
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {MODULES.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.1} className="bg-mac-base">
                <div className="card-tech flex h-full flex-col items-start border-0" data-testid={`laic-module-${i}`}>
                  <span className="mb-6 grid h-12 w-12 place-items-center border border-mac-accent/50 bg-mac-accent/10">
                    <m.icon className="h-5 w-5 text-mac-accent" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wider text-white">{m.title}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Connected to LAIC core</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="03"
            eyebrow="The Mayor's Command Center"
            title={<>It starts the second you sign in.<br />No menus — <span className="text-mac-accent">just decisions</span>.</>}
            sub="Every approval in one clear line. One tap: approved and signed — decisions that took days of paper routing now take seconds. Your whole city, in real time: businesses, properties, collections, every barangay, live."
            testId="laic-command-heading"
          />
          <div className="grid items-start gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <CommandCenter />
            </Reveal>
            <div className="lg:col-span-5">
              {[
                { t: "Every approval in one clear line", d: "Nothing buried, nothing lost in routing — the queue is the agenda." },
                { t: "One tap: approved and signed", d: "Decisions that took days of paper routing now take seconds." },
                { t: "Your whole city, in real time", d: "Businesses, properties, collections, every barangay — live." },
              ].map((p, i) => (
                <Reveal key={p.t} delay={i * 0.12}>
                  <div className="mb-6 border-l-2 border-mac-accent/60 pl-6">
                    <h3 className="font-display text-xl font-semibold uppercase tracking-wider text-white">{p.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.d}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.36}>
                <p className="mt-8 font-display text-2xl font-semibold uppercase tracking-wide text-mac-accent" data-testid="laic-command-quote">
                  "For the first time, nothing in your town is hidden from you."
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-mac-surface/30 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="04"
            eyebrow="Project Accountability"
            title={<>Every project,<br /><span className="text-mac-accent">accounted for</span>.</>}
            testId="laic-accountability-heading"
          />
          <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {ACCOUNTABILITY.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.1} className="bg-mac-base">
                <div className="card-tech h-full border-0" data-testid={`laic-accountability-${i}`}>
                  <a.icon className="mb-6 h-6 w-6 text-mac-accent" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wider text-white">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-12 font-display text-2xl font-semibold uppercase tracking-wide text-white sm:text-3xl" data-testid="laic-accountability-tagline">
              Governance you can <span className="text-mac-accent">show</span>, not just claim.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="05"
            eyebrow="For the People You Serve"
            title={<>No more falling in line.<br />No more <span className="text-mac-accent">"come back tomorrow."</span></>}
            testId="laic-citizens-heading"
          />
          <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {CITIZENS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1} className="bg-mac-base">
                <div className="card-tech h-full border-0" data-testid={`laic-citizen-${i}`}>
                  <c.icon className="mb-6 h-6 w-6 text-mac-accent" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wider text-white">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-mac-surface/30 py-20 lg:py-32" data-testid="laic-payments">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="06"
            eyebrow="Payments"
            title={<>Your money. Your banks.<br /><span className="text-mac-accent">Your control.</span></>}
            sub="LAIC works with the LGU's own existing government bank accounts. No middleman. No outside gateway holding public money."
            testId="laic-payments-heading"
          />
          <div className="grid gap-10 md:grid-cols-3" data-testid="laic-payments-steps">
            {PAY_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="relative">
                  {i < PAY_STEPS.length - 1 && <span className="glow-line absolute left-14 top-6 hidden w-[calc(100%-3rem)] md:block" />}
                  <span className="relative grid h-12 w-12 place-items-center rounded-full border border-mac-accent/60 bg-mac-accent/10 font-mono text-sm text-mac-accent shadow-[0_0_20px_rgb(var(--mac-accent-rgb)/0.2)]">
                    {s.n}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold uppercase tracking-wider text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex gap-4 border border-mac-accent/40 bg-mac-accent/5 p-6" data-testid="laic-payments-callout">
              <ShieldCheck className="h-6 w-6 shrink-0 text-mac-accent" strokeWidth={1.5} />
              <p className="font-display text-xl font-semibold uppercase tracking-wider text-white">
                Full treasury control — LAIC <span className="text-mac-accent">never touches, holds, or routes</span> public money.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 py-20 lg:py-32" data-testid="laic-security">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="07"
            eyebrow="Security"
            title={<>"If everything lives in one system —<br /><span className="text-mac-accent">is it safe?"</span></>}
            sub="Let's answer that completely, and take the fear off the table. Your data passes three walls before anyone reaches it."
            testId="laic-security-heading"
          />
          <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {WALLS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1} className="bg-mac-base">
                <div className="card-tech h-full border-0" data-testid={`laic-wall-${i}`}>
                  <div className="mb-6 flex items-start justify-between">
                    <w.icon className="h-6 w-6 text-mac-accent" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-slate-600">WALL 0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wider text-white">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2" data-testid="laic-security-strip">
              {SECURITY_STRIP.map((s) => (
                <div key={s} className="flex items-center gap-3 bg-mac-base px-6 py-4">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-mac-accent" strokeWidth={1.5} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-300">{s}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-12 font-display text-2xl font-semibold uppercase tracking-wide text-white sm:text-3xl">
              Fully within the law — your data is <span className="text-mac-accent">safer here</span> than it has ever been anywhere.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-mac-surface/30 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            index="08"
            eyebrow="Rollout"
            title={<>You'll never face this alone —<br />a guided <span className="text-mac-accent">two-month rollout</span>.</>}
            sub="Your public services can go live on day one."
            testId="laic-rollout-heading"
          />
          <Reveal>
            <ProcessSteps steps={ROLLOUT} testId="laic-rollout" />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 py-24 lg:py-36" data-testid="laic-closing">
        <div className="mx-auto max-w-5xl px-6 text-left lg:px-8">
          <Reveal>
            <p className="border-l-2 border-mac-accent pl-8 font-display text-3xl font-semibold uppercase leading-tight tracking-wide text-white sm:text-4xl lg:text-5xl">
              "Right now, somewhere in this country, one mayor is about to decide to be the first to run
              their whole city this way.{" "}
              <span className="text-mac-accent">The only question left is whether that mayor is you."</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/contact" data-testid="laic-closing-cta" className="btn-primary mt-12">
              Let's bring your city online <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
